import React, { useEffect, useState, useCallback, useMemo } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  where,
  getDocs,
  limit,
  setDoc,
  updateDoc,
  serverTimestamp
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  FaSearch,
  FaFire,
  FaStar,
  FaUsers,
  FaLock,
  FaGlobe,
  FaPlus,
  FaFilter,
  FaChevronRight,
  FaUserCheck,
  FaUserPlus
} from "react-icons/fa";
import styles from "../styles/ClubsList.module.css";

export default function ClubsList() {
  const [clubs, setClubs] = useState([]);
  const [myClubs, setMyClubs] = useState([]);
  const [trendingClubs, setTrendingClubs] = useState([]);
  const [suggestedClubs, setSuggestedClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("members");
  const navigate = useNavigate();

  const categories = [
    { id: "all", name: "All", icon: "🌐" },
    { id: "coding", name: "Coding", icon: "💻" },
    { id: "sports", name: "Sports", icon: "⚽" },
    { id: "music", name: "Music", icon: "🎵" },
    { id: "art", name: "Art", icon: "🎨" },
    { id: "gaming", name: "Gaming", icon: "🎮" },
    { id: "education", name: "Education", icon: "📚" },
    { id: "business", name: "Business", icon: "💼" },
    { id: "entertainment", name: "Entertainment", icon: "🎬" },
    { id: "technology", name: "Tech", icon: "🔧" },
    { id: "science", name: "Science", icon: "🔬" },
    { id: "food", name: "Food", icon: "🍕" }
  ];

  const sortOptions = [
    { id: "members", label: "Most Members", icon: <FaUsers /> },
    { id: "trending", label: "Trending", icon: <FaFire /> },
    { id: "newest", label: "Newest", icon: <FaStar /> },
    { id: "alphabetical", label: "A-Z", icon: "A" }
  ];

  const getCategoryIcon = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.icon : "👥";
  };



  const fetchUserClubs = useCallback(async (userId, allClubs) => {
    try {
      const membersQuery = query(
        collection(db, "club_members"),
        where("userId", "==", userId),
        where("status", "==", "active")
      );

      const snapshot = await getDocs(membersQuery);
      const memberClubs = snapshot.docs.map(doc => doc.data().clubId);

      const userClubsData = allClubs.filter(club =>
        memberClubs.includes(club.id)
      );
      setMyClubs(userClubsData);
    } catch (err) {
      console.error("Error fetching user clubs:", err);
    }
  }, []); // Static dependency? or none.

  const getSuggestedClubs = useCallback(async (userId, allClubs) => {
    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userInterests = userData.interests || [];
        // Note: This relies on myClubs state which might be stale if called immediately 
        // after fetchUserClubs in the same tick, but here we pass 'allClubs' from snapshot.
        // We'll rely on the snapshot data.
        // Since myClubs is state, we need it in dependency if we use it.
        // But to avoid infinite loop with useEffect, we need to be careful.
        // Actually, we can fetch myClubs IDs fresh here if we wanted, but let's stick to simple fix.

        // We can't easily access 'myClubs' state here without it being a dependency.
        // And if it is a dependency, it changes, triggering this, triggering useEffect?
        // Let's rely on fetching member clubs fresh or passing them as arg?
        // For now, I will add myClubs as dependency.
        const joinedClubIds = myClubs.map(club => club.id);

        // Suggest clubs based on user interests and similar users
        const suggested = allClubs
          .filter(club => !joinedClubIds.includes(club.id))
          .filter(club => {
            // Prioritize clubs matching user interests
            if (userInterests.length > 0) {
              return userInterests.includes(club.category);
            }
            // Otherwise suggest popular clubs
            return (club.memberCount || 0) > 10;
          })
          .sort((a, b) => (b.memberCount || 0) - (a.memberCount || 0))
          .slice(0, 6);

        setSuggestedClubs(suggested);
      }
    } catch (err) {
      console.error("Error getting suggestions:", err);
    }
  }, [myClubs]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser);
    });

    // Fetch all clubs with real-time updates
    const clubsQuery = query(
      collection(db, "clubs"),
      orderBy("memberCount", "desc"),
      limit(50) // Limit for performance
    );

    const unsubscribeClubs = onSnapshot(clubsQuery, async (snapshot) => {
      const clubsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      setClubs(clubsData);

      // Get trending clubs (most members with recent activity)
      const trending = [...clubsData]
        .sort((a, b) => {
          // Sort by recent activity descending
          const dateA = a.recentActivity?.toDate?.() || a.createdAt || new Date(0);
          const dateB = b.recentActivity?.toDate?.() || b.createdAt || new Date(0);
          return dateB - dateA;
        })
        .slice(0, 8);
      setTrendingClubs(trending);

      // Fetch user's clubs if logged in
      if (user) {
        fetchUserClubs(user.uid, clubsData);
        // Get suggested clubs based on user's interests
        getSuggestedClubs(user.uid, clubsData);
      } else {
        setMyClubs([]);
        setSuggestedClubs([]);
      }

      setLoading(false);
    }, (error) => {
      console.error("Error fetching clubs:", error);
      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      unsubscribeClubs();
    };
  }, [user, fetchUserClubs, getSuggestedClubs]);

  const handleJoinClub = async (clubId) => {
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      // Check if already a member
      const memberQuery = query(
        collection(db, "club_members"),
        where("userId", "==", user.uid),
        where("clubId", "==", clubId)
      );
      const existingMember = await getDocs(memberQuery);

      if (existingMember.empty) {
        // Add user to club
        const memberRef = doc(collection(db, "club_members"));
        await setDoc(memberRef, {
          userId: user.uid,
          clubId: clubId,
          role: "member",
          joinedAt: new Date(),
          status: "active"
        });

        // Update club member count
        const clubRef = doc(db, "clubs", clubId);
        const clubDoc = await getDoc(clubRef);
        if (clubDoc.exists()) {
          const currentCount = clubDoc.data().memberCount || 0;
          await updateDoc(clubRef, {
            memberCount: currentCount + 1,
            recentActivity: serverTimestamp()
          });
        }

        // Update local state
        const clubToJoin = clubs.find(club => club.id === clubId);
        if (clubToJoin) {
          setMyClubs(prev => [...prev, { ...clubToJoin, memberCount: (clubToJoin.memberCount || 0) + 1 }]);
        }
      }
    } catch (err) {
      console.error("Error joining club:", err);
      alert("Failed to join club. Please try again.");
    }
  };

  const filteredAndSortedClubs = useMemo(() => {
    let filtered = clubs.filter(club => {
      const matchesSearch = club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        club.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        club.category?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === "all" || club.category === filterCategory;
      return matchesSearch && matchesCategory;
    });

    // Apply sorting
    switch (sortBy) {
      case "members":
        filtered.sort((a, b) => (b.memberCount || 0) - (a.memberCount || 0));
        break;
      case "trending":
        filtered.sort((a, b) => {
          const scoreA = (a.memberCount || 0) + (a.recentActivity || 0);
          const scoreB = (b.memberCount || 0) + (b.recentActivity || 0);
          return scoreB - scoreA;
        });
        break;
      case "newest":
        filtered.sort((a, b) => b.createdAt - a.createdAt);
        break;
      case "alphabetical":
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    return filtered;
  }, [clubs, searchTerm, filterCategory, sortBy]);

  const debouncedSearch = useCallback((value) => {
    // Simple debounce implementation
    const handler = setTimeout(() => {
      setSearchTerm(value);
    }, 300);
    return () => clearTimeout(handler);
  }, []);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <p>Loading channels...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header & Search */}
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>Explore Channels</h1>
        <div className={styles.searchBar}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search channels..."
            onChange={(e) => debouncedSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      {/* Categories & Filters */}
      <div className={styles.controls}>
        <div className={styles.categoryScroll}>
          <button
            className={`${styles.categoryChip} ${filterCategory === "all" ? styles.categoryChipActive : ''}`}
            onClick={() => setFilterCategory("all")}
          >
            All
          </button>
          {categories.slice(1).map(category => (
            <button
              key={category.id}
              className={`${styles.categoryChip} ${filterCategory === category.id ? styles.categoryChipActive : ''}`}
              onClick={() => setFilterCategory(category.id)}
            >
              <span className={styles.categoryIcon}>{category.icon}</span>
              {category.name}
            </button>
          ))}
        </div>

        <div className={styles.filterActions}>
          <select
            className={styles.sortSelect}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            {sortOptions.map(option => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
          <button
            onClick={() => navigate("/create-club")}
            className={styles.createBtn}
          >
            <FaPlus /> Create
          </button>
        </div>
      </div>

      {/* Main Grid */}
      <div className={styles.gridContainer}>
        {filteredAndSortedClubs.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>🔍</div>
            <h3>No channels found</h3>
            <p>Try adjusting your search or filters</p>
            <button
              className={styles.resetBtn}
              onClick={() => {
                setSearchTerm("");
                setFilterCategory("all");
                setSortBy("members");
              }}
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className={styles.clubsGrid}>
            {filteredAndSortedClubs.map(club => (
              <ClubCard
                key={club.id}
                club={club}
                navigate={navigate}
                user={user}
                onJoin={handleJoinClub}
                getCategoryIcon={getCategoryIcon}
                isMember={myClubs.some(c => c.id === club.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Simplified Club Card Component
function ClubCard({ club, navigate, user, isMember, onJoin, getCategoryIcon }) {
  const formatMemberCount = (count) => {
    if (!count) return "0";
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
    return count;
  };

  const handleCardClick = (e) => {
    if (e.target.closest('button')) {
      e.stopPropagation();
      return;
    }
    navigate(`/club/${club.id}`);
  };

  const handleJoinClick = (e) => {
    e.stopPropagation();
    if (onJoin) {
      onJoin(club.id);
    }
  };

  return (
    <div className={styles.card} onClick={handleCardClick}>
      <div className={styles.cardHeader}>
        {club.bannerUrl ? (
          <img src={club.bannerUrl} className={styles.cardBanner} alt={club.name} />
        ) : (
          <div className={styles.cardBanner} style={{ background: club.themeColor || '#2a2a2a' }} />
        )}
        <div className={styles.cardPrivacy}>
          {club.privacy === 'private' ? <FaLock size={12} /> : <FaGlobe size={12} />}
        </div>
      </div>

      <div className={styles.cardContent}>
        <div className={styles.cardTop}>
          {club.logoUrl ? (
            <img src={club.logoUrl} alt={club.name} className={styles.cardAvatar} />
          ) : (
            <div className={styles.cardAvatarPlaceholder}>
              {club.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className={styles.cardInfo}>
            <h3 className={styles.cardTitle}>{club.name}</h3>
            <div className={styles.cardMeta}>
              <span className={styles.cardCategory}>{club.category || 'General'}</span>
              <span className={styles.cardDot}>•</span>
              <span className={styles.cardMembers}>{formatMemberCount(club.memberCount)} members</span>
            </div>
          </div>
        </div>

        <p className={styles.cardDesc}>
          {club.description || "Join this community to connect and share"}
        </p>

        <div className={styles.cardFooter}>
          {isMember ? (
            <button className={`${styles.joinBtn} ${styles.joinedBtn}`}>
              Following
            </button>
          ) : (
            <button className={styles.joinBtn} onClick={handleJoinClick}>
              Join
            </button>
          )}
        </div>
      </div>
    </div>
  );
}